import { Controller } from "@hotwired/stimulus"

const REDUCED = "(prefers-reduced-motion: reduce)"

/**
 * The hero: a draft writing itself in the real editor's chrome.
 *
 * Copy lives in the markup, not here — each `.lp-type` carries a ghost span with
 * the final text, which also reserves the exact box so the card never resizes
 * while typing. This reads the ghost and plays it back a character at a time.
 *
 * The loop parks itself whenever the stage is off-screen.
 */
export default class extends Controller {
  static targets = ["type", "slash", "slashRow", "code", "state", "dot"]

  static values = {
    charDelay: { type: Number, default: 26 },
    hold: { type: Number, default: 4600 }
  }

  connect() {
    if (window.matchMedia(REDUCED).matches) return

    this.cancelled = false
    this.visible = true

    this.blocks = this.typeTargets.map((el) => ({
      wrap: el,
      out: el.querySelector(".lp-type__out"),
      text: (el.querySelector(".lp-type__ghost")?.textContent || "").replace(/\s+/g, " ").trim()
    }))

    this.watchVisibility()
    this.run()
  }

  disconnect() {
    this.cancelled = true
    if (this.observer) this.observer.disconnect()
  }

  watchVisibility() {
    this.observer = new IntersectionObserver(
      ([entry]) => {
        this.visible = entry.isIntersecting
      },
      { threshold: 0.15 }
    )
    this.observer.observe(this.element)
  }

  async run() {
    while (!this.cancelled) {
      await this.playOnce()
      if (this.cancelled) return
      await this.sleep(this.holdValue)
      this.reset()
      await this.sleep(500)
    }
  }

  async playOnce() {
    await this.idleUntilVisible()
    if (this.cancelled) return

    this.setState("new draft", false)

    for (const [index, block] of this.blocks.entries()) {
      await this.typeBlock(block)
      if (this.cancelled) return
      if (index === 0) await this.sleep(360)
    }

    await this.save()
    if (this.cancelled) return

    await this.sleep(420)
    await this.showSlashMenu()
    if (this.cancelled) return

    if (this.hasCodeTarget) this.codeTarget.classList.add("is-in")
    await this.save()
  }

  async typeBlock({ wrap, out, text }) {
    if (!out) return
    wrap.classList.add("is-typing")
    out.textContent = ""

    for (let i = 0; i < text.length; i++) {
      if (this.cancelled) return
      out.textContent = text.slice(0, i + 1)
      // Punctuation gets a beat; spaces move quicker. Even cadence reads robotic.
      const char = text[i]
      let delay = this.charDelayValue
      if (char === " ") delay *= 0.55
      if (char === "," || char === "—") delay *= 5
      if (char === "." || char === "?") delay *= 9
      await this.sleep(delay)
    }

    wrap.classList.remove("is-typing")
  }

  async showSlashMenu() {
    if (!this.hasSlashTarget) return

    this.slashTarget.classList.add("is-open")
    await this.sleep(340)

    for (let step = 0; step < this.slashRowTargets.length; step++) {
      if (this.cancelled) return
      this.slashRowTargets.forEach((row, i) => row.classList.toggle("is-active", i === step))
      await this.sleep(320)
    }

    await this.sleep(220)
    this.slashTarget.classList.remove("is-open")
    await this.sleep(200)
  }

  async save() {
    this.setState("saving", true)
    await this.sleep(780)
    if (this.cancelled) return
    this.setState("saved just now", false)
  }

  setState(label, busy) {
    if (this.hasStateTarget) this.stateTarget.textContent = label
    if (this.hasDotTarget) this.dotTarget.classList.toggle("is-busy", busy)
  }

  reset() {
    this.blocks.forEach(({ wrap, out }) => {
      wrap.classList.remove("is-typing")
      if (out) out.textContent = ""
    })
    if (this.hasCodeTarget) this.codeTarget.classList.remove("is-in")
    if (this.hasSlashTarget) this.slashTarget.classList.remove("is-open")
    this.slashRowTargets.forEach((row) => row.classList.remove("is-active"))
  }

  async idleUntilVisible() {
    while (!this.visible && !this.cancelled) await this.sleep(300)
  }

  sleep(ms) {
    return new Promise((resolve) => {
      const id = setTimeout(resolve, ms)
      if (this.cancelled) {
        clearTimeout(id)
        resolve()
      }
    })
  }
}
